import axios from "axios";
import { API_URL } from "../constant";

const API_BASE_URL = API_URL || "https://staging.api.topcoinuk.com/api";

// Create axios instance with auth headers
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("auth_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const retailerService = {
  // Get all retailers with pagination and filters
  getAllRetailers: async (params = {}) => {
    const response = await api.get("/superadmin/retail-businesses", { params });
    return response.data;
  },

  // Get specific retailer details
  getRetailer: async (userId) => {
    const response = await api.get(`/superadmin/retail-businesses/${userId}`);
    return response.data;
  },

  // Create new retailer
  createRetailer: async (retailerData) => {
    const response = await api.post(
      "/superadmin/retail-businesses",
      retailerData
    );
    return response.data;
  },

  // Update retailer - FIXED: Use POST instead of PUT for Laravel form data
  updateRetailer: async (userId, retailerData) => {
    const response = await api.post(
      `/superadmin/retail-businesses/${userId}`,
      retailerData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  // Delete retailer
  deleteRetailer: async (userId) => {
    const response = await api.delete(
      `/superadmin/retail-businesses/${userId}`
    );
    return response.data;
  },

  // Update retailer status
  updateRetailerStatus: async (userId, statusData) => {
    const response = await api.post(
      `/superadmin/retail-businesses/${userId}/status`,
      statusData
    );
    return response.data;
  },

  // Get retailer status history
  getRetailerStatusHistory: async (userId) => {
    const response = await api.get(
      `/superadmin/retail-businesses/${userId}/status-history`
    );
    return response.data;
  },

  // Get retailer wallet details
  getRetailerWallet: async (userId) => {
    const response = await api.get(
      `/superadmin/retail-businesses/${userId}/wallet`
    );
    return response.data;
  },

  // Reset retailer password
  resetRetailerPassword: async (userId, passwordData) => {
    const response = await api.post(
      `/superadmin/retail-businesses/${userId}/reset-password`,
      passwordData
    );
    return response.data;
  },

  // Get retailer transaction history
  getRetailerTransactionHistory: async (retailerId, params = {}) => {
    try {
      const response = await api.get(
        `/superadmin/retail-businesses/${retailerId}/transaction-history`,
        { params }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Check email availability
  checkEmailAvailability: async (email) => {
    try {
      const response = await api.post(
        "/superadmin/retail-businesses/check-email-availability",
        { email }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Check phone availability
  checkPhoneAvailability: async (phone) => {
    try {
      const response = await api.post(
        "/superadmin/retail-businesses/check-phone-availability",
        { phone }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get retailer shop details
  getRetailerShopDetails: async (retailerId) => {
    try {
      const response = await api.get(
        `/superadmin/retail-businesses/${retailerId}/shop-details`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Key Management Methods - FIXED endpoints
  getPublicKey: async (retailerId) => {
    const response = await api.get(`/superadmin/retail-business/${retailerId}/public-key`);
    return response.data;
  },

  validateKeys: async (retailerId) => {
    const response = await api.get(`/superadmin/retail-business/${retailerId}/validate-keys`);
    return response.data;
  },

  regenerateKeyPair: async (retailerId, algorithm = 'rsa') => {
    const response = await api.post(`/superadmin/retail-business/${retailerId}/regenerate-keys`, {
      algorithm
    });
    return response.data;
  },

  getKeyStatus: async (retailerId) => {
    const response = await api.get(`/superadmin/retail-business/${retailerId}/key-status`);
    return response.data;
  },

  getRetailBusinessCount: async (filters = {}) => {
    try {
      const response = await api.get('/superadmin/count/retail-businesses', {
        params: filters,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching retail business count:', error);
      throw error;
    }
  }
};